import type {
  CreateAddressRequest,
  UpdateAddressRequest,
} from '@/api/addresses/addressMutations';
import {
  createAddressMutation,
  updateAddressMutation,
} from '@/api/addresses/addressMutations';
import { countriesQuery } from '@/api/countries/countryQueries';
import { currentUserQuery } from '@/api/user/userQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import type { Country } from '@/domain/Country';
import { defaultAddressKey } from '@/domain/User';
import { useFormMutation } from '@/hooks/useFormMutation';
import { createAddressSchema } from '@/validation/addressValidator';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useState } from 'react';
import type * as z from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

type AddressFormValues = z.infer<typeof createAddressSchema>;

interface CreateAddressFormDialogProps {
  trigger?: React.ReactNode;
  onAddressCreated?: (addressName: string) => void;
}

export function CreateAddressFormDialog({
  trigger,
  onAddressCreated,
}: CreateAddressFormDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const { data: countriesData } = useQuery(countriesQuery);
  const { data: user } = useQuery(currentUserQuery);

  const defaultAddress = user?.data.addresses.find(
    (addr) => addr.name === defaultAddressKey,
  );

  const createAddress = useFormMutation(createAddressMutation());
  const updateAddress = useFormMutation(
    updateAddressMutation(defaultAddress?.id ?? 0),
  );

  const initialValues: AddressFormValues = {
    line1: '',
    city: '',
    country: '',
    zipcode: '',
    line2: '',
  };

  const formik = useFormik<AddressFormValues>({
    initialValues,
    validate: toFormikValidate(createAddressSchema),
    onSubmit: async (values) => {
      const countryName = countriesData?.data.find(
        (c: Country) => c.country_code === values.country,
      )?.country;

      if (setAsDefault && defaultAddress) {
        const payload: UpdateAddressRequest = {
          ...values,
          name: defaultAddressKey,
          country: countryName || values.country,
          line2: values.line2 || null,
        };

        await updateAddress.mutateAsync(payload);

        if (onAddressCreated) {
          onAddressCreated(defaultAddressKey);
        }
      } else {
        const randomName = `Address_${Date.now()}_${Math.random().toString(36).substring(7)}`;
        const payload: CreateAddressRequest = {
          ...values,
          name: randomName,
          country: countryName || values.country,
          line2: values.line2 || null,
        };
        await createAddress.mutateAsync(payload);
        if (onAddressCreated) {
          onAddressCreated(randomName);
        }
      }

      setIsDialogOpen(false);
      formik.resetForm();
      setSetAsDefault(false);
    },
  });

  const countryOptions = countriesData?.data
    ? countriesData.data.map((country) => ({
        label: country.country,
        value: country.country_code,
      }))
    : [];

  const isPending = createAddress.isPending || updateAddress.isPending;

  return (
    <>
      {trigger && (
        <div onClick={() => setIsDialogOpen(true)} className="cursor-pointer">
          {trigger}
        </div>
      )}
      <DialogWrapper
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title="Add new shipping address"
        maxWidth="xl"
      >
        <form onSubmit={formik.handleSubmit} className="space-y-4">
          <Select
            id="country"
            label="Country"
            name="country"
            value={formik.values.country}
            onChange={(val: string) => formik.setFieldValue('country', val)}
            onBlur={formik.handleBlur}
            options={countryOptions}
            error={
              formik.touched.country && formik.errors.country
                ? formik.errors.country
                : undefined
            }
            touched={!!formik.touched.country}
          />
          <Input
            id="line1"
            label="Address Line*"
            type="text"
            {...formik.getFieldProps('line1')}
            error={
              formik.touched.line1 && formik.errors.line1
                ? formik.errors.line1
                : undefined
            }
            touched={!!formik.touched.line1}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              id="city"
              label="City*"
              type="text"
              {...formik.getFieldProps('city')}
              error={
                formik.touched.city && formik.errors.city
                  ? formik.errors.city
                  : undefined
              }
              touched={!!formik.touched.city}
            />
            <Input
              id="zipcode"
              label="Zip / Postal Code*"
              type="text"
              {...formik.getFieldProps('zipcode')}
              error={
                formik.touched.zipcode && formik.errors.zipcode
                  ? formik.errors.zipcode
                  : undefined
              }
              touched={!!formik.touched.zipcode}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="set-as-default"
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
              className="size-4 cursor-pointer"
            />
            <label htmlFor="set-as-default" className="cursor-pointer text-sm">
              Set as default shipping address
            </label>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={isPending}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </form>
      </DialogWrapper>
    </>
  );
}
